import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * A wrapper for routes that require authentication.
 * Redirects to the login page if the user is not authenticated.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const { isLoggedIn, isLoading } = useAuth();
  
  // While checking authentication status, show nothing to prevent flash of content
  if (isLoading) {
    return null;
  }
  
  if (!isLoggedIn) {
    // Redirect to the login page, but save the current location they were
    // trying to go to when they were redirected. This allows us
    // to send them to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Wrap children in a Fragment to ensure we always return a valid React element
  return <>{children}</>;
};

export default ProtectedRoute;
